/**
 * SOLID Review
 * S — Strong. One concern: a use case that is one decision in one step of the serial section, its changes saved.
 * O — Strong. The decision is handed in; nothing here names one.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. A step of the serial section and the decision, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IServiceSerialStep, TDecision } from './ClientSteps';

/** The decision a one-step use case asks, over the input its step reads. */
type TStepDecision<TInput, TCommand> = (ctx: IContextRequest, input: TInput, command: TCommand) => Promise<TDecision<unknown>>;

/**
 * ONE DECISION, ONE STEP (frontend-application-oo P3, 2026-10-28): the state loaded, the decision asked, its changes saved
 * (and kept) — a server event of the table, a funnel button, a media toggle. A decision that changes nothing saves nothing,
 * and is answered as done like any other.
 */
export class DecisionStep<TInput, TCommand> implements IUseCase<TCommand, TPortOutcome> {
  constructor(private readonly step: IServiceSerialStep<TInput>, private readonly decide: TStepDecision<TInput, TCommand>) {}

  async execute(ctx: IContextRequest, command: TCommand): Promise<TPortOutcome> {
    return this.step.outcome(await this.step.run(ctx, (input) => this.decide(ctx, input, command)));
  }
}
